import { createServerFn } from "@tanstack/react-start";
import { readQuizAnswersFromSearch } from "@wags/matchmaker/pure.ts";
import { quizAnswersContract } from "@wags/matchmaker/quiz/quiz.contract.ts";
import { matchSearchFiltersSchema } from "@wags/matchmaker/scoring/match-score.contract.ts";
import type { BoundaryUnknown } from "@wags/utils/boundary-unknown.ts";
import { z } from "zod";
import { resolveViewerProfileIdentity } from "@/lib/core/auth";
import { getMatchmaker } from "@/lib/core/matchmaker-core.server";
import type {
    SearchParams,
    SearchResult,
    SearchResultItem,
} from "@/features/discovery";
import { searchListings } from "@/features/discovery";

export type ScoredListingItem = SearchResultItem & {
    matchScore: number;
    explanation: string;
};

export type ScoredSearchResult = Omit<SearchResult, "results"> & {
    results: ScoredListingItem[];

    scored: boolean;

    pickerInfluenced?: boolean;
};

const MAX_VISIBLE_RESULT_LIMIT = 51;

const scoreListingsForMatchInputSchema = z.object({
    searchParams: matchSearchFiltersSchema.extend({
        limit: z.number().int().min(1).max(MAX_VISIBLE_RESULT_LIMIT).optional(),
    }),
    quizAnswers: quizAnswersContract.nullable(),
});

const DEFAULT_VISIBLE_RESULT_LIMIT = 51;
const MATCH_SCORING_CANDIDATE_LIMIT = 200;

export function buildVisibleResult(
    searchResult: SearchResult,
    results: ScoredListingItem[],
    visibleLimit: number,
): ScoredSearchResult {
    const visibleResults = results.slice(0, visibleLimit);
    const hasMoreResults = searchResult.total > visibleResults.length;

    return {
        ...searchResult,
        results: visibleResults,
        limit: visibleLimit,
        offset: 0,
        page: 0,
        prevOffset: null,
        nextOffset: hasMoreResults ? visibleResults.length : null,
        nextPage: hasMoreResults ? 1 : null,
        scored: true,
    };
}

const scoreListingsForMatch = createServerFn({ method: "GET" })
    .inputValidator((data: BoundaryUnknown) =>
        scoreListingsForMatchInputSchema.parse(data),
    )
    .handler(async ({ data }): Promise<ScoredSearchResult> => {
        const visibleLimit = Math.min(
            MAX_VISIBLE_RESULT_LIMIT,
            Math.max(
                1,
                data.searchParams.limit ?? DEFAULT_VISIBLE_RESULT_LIMIT,
            ),
        );
        const quizAnswers = data.quizAnswers;
        const hasQuizAnswers =
            quizAnswers != null && Object.keys(quizAnswers).length > 0;
        const appUserId = hasQuizAnswers
            ? (await resolveViewerProfileIdentity())?.appUserId
            : undefined;
        const searchResult: SearchResult = await searchListings({
            data: hasQuizAnswers
                ? {
                      ...data.searchParams,
                      limit: Math.max(
                          visibleLimit,
                          MATCH_SCORING_CANDIDATE_LIMIT,
                      ),
                      offset: 0,
                      page: 0,
                  }
                : data.searchParams,
        });
        const outcome = await (
            await getMatchmaker()
        ).scorer.scoreListings<SearchResultItem>({
            candidates: searchResult.results,
            quizAnswers,
            maxDistanceMiles: data.searchParams.maxDistanceMiles,
            appUserId,
        });
        if (!hasQuizAnswers) {
            return { ...searchResult, results: outcome.results, scored: false };
        }
        if (!outcome.scored) {
            return {
                ...buildVisibleResult(
                    searchResult,
                    outcome.results,
                    visibleLimit,
                ),
                scored: false,
                pickerInfluenced: outcome.pickerInfluenced,
            };
        }
        return {
            ...buildVisibleResult(searchResult, outcome.results, visibleLimit),
            pickerInfluenced: outcome.pickerInfluenced,
        };
    });

export function buildMatchScoreQuery(searchParams: SearchParams) {
    const quizAnswers = readQuizAnswersFromSearch(searchParams);
    return {
        queryKey: ["match-scored", searchParams] as const,
        queryFn: () =>
            scoreListingsForMatch({
                data: { searchParams, quizAnswers },
            }),
        staleTime: 60_000,
    };
}
